import React, { createContext, useState, useEffect, useContext } from 'react';
import { useColorScheme } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const systemColorScheme = useColorScheme();
  const [themeMode, setThemeMode] = useState('system'); // 'light', 'dark', 'system'
  const [currentTheme, setCurrentTheme] = useState('light');

  // Load theme preference from storage
  useEffect(() => {
    loadThemePreference();
  }, []);

  // Update current theme when mode or system preference changes
  useEffect(() => {
    const newTheme = themeMode === 'system' ? (systemColorScheme || 'light') : themeMode;
    setCurrentTheme(newTheme);
  }, [themeMode, systemColorScheme]);

  const loadThemePreference = async () => {
    try {
      const savedTheme = await AsyncStorage.getItem('themeMode');
      if (savedTheme) {
        setThemeMode(savedTheme);
      }
    } catch (error) {
      console.error('Error loading theme preference:', error);
    }
  };

  const saveThemePreference = async (mode) => {
    try {
      await AsyncStorage.setItem('themeMode', mode);
      setThemeMode(mode);
    } catch (error) {
      console.error('Error saving theme preference:', error);
    }
  };

  const colors = {
    light: {
      // Backgrounds
      primary: '#ffffff',
      secondary: '#f8fafc',
      tertiary: '#f1f5f9',
      elevated: 'rgba(255, 255, 255, 0.95)',
      card: '#ffffff',
      glass: 'rgba(255, 255, 255, 0.8)',
      
      // Text
      text: '#1e293b',
      textSecondary: '#64748b',
      textTertiary: '#94a3b8',
      textInverse: '#ffffff',
      
      // Borders
      border: '#e2e8f0',
      borderMedium: '#cbd5e1',
      borderStrong: '#94a3b8',
      
      // Interactive
      hover: '#f1f5f9',
      active: '#e2e8f0',
      disabled: '#f8fafc',
      
      // Brand
      brand: '#667eea',
      brandLight: '#a8b5ff',
      brandDark: '#4c5fd5',
      accent: '#4a9eff',
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444',
    },
    dark: {
      // Backgrounds
      primary: '#0a0a0a',
      secondary: '#1a1a1a',
      tertiary: '#2a2a2a',
      elevated: 'rgba(26, 26, 26, 0.95)',
      card: '#1a1a1a',
      glass: 'rgba(26, 26, 26, 0.8)',
      
      // Text
      text: '#f1f5f9',
      textSecondary: '#cbd5e1',
      textTertiary: '#94a3b8',
      textInverse: '#0a0a0a',
      
      // Borders
      border: '#333333',
      borderMedium: '#444444',
      borderStrong: '#555555',
      
      // Interactive
      hover: '#2a2a2a',
      active: '#333333',
      disabled: '#1a1a1a',
      
      // Brand
      brand: '#818cf8',
      brandLight: '#a5b4fc',
      brandDark: '#6366f1',
      accent: '#60a5fa',
      success: '#34d399',
      warning: '#fbbf24',
      error: '#f87171',
    },
  };

  const gradients = {
    light: {
      primary: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      secondary: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      tertiary: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #ffffff 50%, #f0f4ff 100%)',
      card: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
    },
    dark: {
      primary: 'linear-gradient(135deg, #818cf8 0%, #a78bfa 100%)',
      secondary: 'linear-gradient(135deg, #f472b6 0%, #fb923c 100%)',
      tertiary: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)',
      background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 50%, #0a0a0a 100%)',
      card: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
    },
  };

  const glass = {
    light: {
      backgroundColor: 'rgba(255, 255, 255, 0.7)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      borderWidth: 1,
      borderColor: 'rgba(255, 255, 255, 0.5)',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
    },
    dark: {
      backgroundColor: 'rgba(30, 41, 59, 0.7)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      borderWidth: 1,
      borderColor: 'rgba(255, 255, 255, 0.1)',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
    },
  };

  const shadows = {
    light: {
      small: '0 2px 8px rgba(0, 0, 0, 0.1)',
      medium: '0 4px 16px rgba(0, 0, 0, 0.12)',
      large: '0 8px 32px rgba(0, 0, 0, 0.15)',
      glow: '0 0 0 3px rgba(102, 126, 234, 0.1)',
    },
    dark: {
      small: '0 2px 8px rgba(0, 0, 0, 0.3)',
      medium: '0 4px 16px rgba(0, 0, 0, 0.4)',
      large: '0 8px 32px rgba(0, 0, 0, 0.5)',
      glow: '0 0 0 3px rgba(129, 140, 248, 0.2)',
    },
  };

  const value = {
    themeMode,
    currentTheme,
    isDark: currentTheme === 'dark',
    colors: colors[currentTheme],
    gradients: gradients[currentTheme],
    glass: glass[currentTheme],
    shadows: shadows[currentTheme],
    setTheme: saveThemePreference,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export default ThemeContext;

